import type { FormEvent } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Divider, Grid, makeStyles } from '@material-ui/core';
import type { Theme } from '@material-ui/core';

import { Conditional } from '@klp/conditional';
import { useAppParams } from '@klp/core/app/useAppParams';
import { Block, Heading, Link } from '@klp/design';
import { ButtonsContainer } from '@klp/design/ButtonsContainer';
import { MimeTypes } from '@klp/file';
import type { FileObject } from '@klp/file/classes';
import { Form, FormButton, FormEditor, FormFiles } from '@klp/form';
import { sortList, formatDateTime } from '@klp/helpers';

import type { Sak, Saksmelding, SaksmeldingInput } from '../classes';

import { SaksmeldingComponent } from './Saksmelding';

const useStyles = makeStyles((_theme: Theme) => ({
    container: {
        marginBottom: '80px',
    },
    fileUploader: {
        width: '600px',
    },
    chip: {},
    form: {},
}));

interface IProps {
    sak: Sak;
    disabled?: boolean;
    onSubmitMelding: (
        event: FormEvent<HTMLFormElement>,
        input: SaksmeldingInput,
    ) => void;
}

export const SakSingle: React.FC<IProps> = ({
    sak,
    disabled,
    onSubmitMelding,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { selectedClient } = useAppParams();

    const [buttonDisabled, setButtonDisabled] = useState(true);
    const [showReplyBox, setShowReplyBox] = useState(false);
    const [showAllMessages, setShowAllMessages] = useState(false);
    const [editorUnmounted, setEditorUnmounted] = useState(false);

    // Reset reply box when new message is added
    useEffect(() => {
        if (!showReplyBox) {
            return;
        }

        setShowReplyBox(false);
        setButtonDisabled(true);
        setEditorUnmounted(true);

        const timer = setTimeout(() => {
            setEditorUnmounted(false);
        }, 100);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sak.saksmeldingList.length]);

    const sortSaksmeldinger = (list: Array<Saksmelding>) =>
        list.sort(sortList<Saksmelding>('created', undefined, 'desc'));

    const toggleShowReplyButton = () => {
        setShowReplyBox((prev) => !prev);
    };

    const toggleShowAllMessages = () => {
        setShowAllMessages((prev) => !prev);
    };

    const onChange = (value: string) => {
        setButtonDisabled(value.length === 0);
    };

    const renderReplyBoxButton = () => {
        return (
            <Block>
                <ButtonsContainer justifyContent="flex-end">
                    <Form>
                        <FormButton
                            name="ShowReply"
                            actionType="button"
                            translation="sak.meldinger.sendSvar"
                            value={t('general.submit')}
                            onClick={toggleShowReplyButton}
                            disableOnError={true}
                        />
                    </Form>
                </ButtonsContainer>
            </Block>
        );
    };

    const renderReplyBox = () => {
        const attachments: Array<FileObject> = [];

        if (editorUnmounted) return null;

        return (
            <Block>
                <Form
                    className={classes.form}
                    onSubmit={onSubmitMelding}
                    values={{ sakId: sak.id }}
                    disabled={disabled}
                >
                    <Grid
                        container={true}
                        spacing={2}
                        className={classes.container}
                    >
                        <Grid
                            item={true}
                            xs={12}
                            key={`${sak.saksmeldingList.length}`}
                        >
                            <FormEditor
                                name="melding"
                                title={t('sak.meldinger.melding.name')}
                                value=""
                                helperText={t(
                                    'sak.meldinger.melding.helperText',
                                )}
                                onChange={onChange}
                                key={`editor-${sak.saksmeldingList.length}`}
                            />
                        </Grid>
                        <Grid item={true} xs={12}>
                            <div className={classes.fileUploader}>
                                <FormFiles
                                    name="sakImagesUploader"
                                    title={t('sak.sakImages.name')}
                                    value={attachments}
                                    mimeTypes={MimeTypes.image}
                                    path=""
                                    helperText={t('sak.sakImages.helperText')}
                                    accept={MimeTypes.image}
                                    queueName="sakImages"
                                />
                            </div>
                        </Grid>

                        <Grid item={true} xs={12}>
                            <div className={classes.fileUploader}>
                                <FormFiles
                                    name="sakFilesUploader"
                                    title={t('sak.sakDocuments.name')}
                                    value={attachments}
                                    mimeTypes={MimeTypes.document}
                                    path=""
                                    helperText={t(
                                        'sak.sakDocuments.helperText',
                                    )}
                                    accept={MimeTypes.document}
                                    queueName="sakAttachments"
                                />
                            </div>
                        </Grid>

                        <Grid item={true} xs={12}>
                            <ButtonsContainer justifyContent="flex-end">
                                <FormButton
                                    name="Send"
                                    actionType="submit"
                                    translation="sak.meldinger.sendSvar"
                                    value={t('general.submit')}
                                    disableOnError={true}
                                    disabled={buttonDisabled}
                                    isLoading={disabled}
                                />
                            </ButtonsContainer>
                        </Grid>
                    </Grid>
                </Form>
            </Block>
        );
    };

    const sortedList = sortSaksmeldinger(sak.saksmeldingList);

    return (
        <>
            <Block marginBottom="compact">
                <Conditional condition={!!selectedClient}>
                    <Heading marginTop="tiny">
                        {t('clientSelect.clientName')}
                        {': '}
                        {selectedClient?.kundeName}
                    </Heading>
                </Conditional>

                <Heading marginTop="tiny">
                    {t('sak.emne')}
                    {': '}
                    <span>{sak.emne}</span>
                </Heading>

                <Block marginTop="tiny">
                    {t('sak.status')}
                    {': '}
                    <Chip
                        label={t(`sak.sakStatus.${sak.status}`, {
                            count: 1,
                        })}
                        variant="outlined"
                        color="secondary"
                        className={classes.chip}
                    />
                </Block>

                <Block marginTop="tiny">
                    {t('sak.saksnummer')}
                    {': '}
                    {sak.saksnummer}
                </Block>
                <Block>
                    {t('sak.buildingcategory.name')}
                    {': '}
                    {sak.sakskategori?.navn}
                </Block>

                {sak.saksmeldingList.length > 0 && (
                    <Block>
                        {t('sak.supportcategory.name')}
                        {': '}
                        {sak.ekstraSakskategoriList
                            .map((item) => item.navn)
                            .join(',')}
                    </Block>
                )}
            </Block>

            {sortedList.length > 0 && (
                <SaksmeldingComponent saksmelding={sortedList[0]} />
            )}

            {!sak.isArchived && !showReplyBox && renderReplyBoxButton()}
            {!sak.isArchived && showReplyBox && renderReplyBox()}

            <Block marginTop="bulky" marginBottom="compact">
                <Divider />
            </Block>

            {sortedList.length > 1 && !showAllMessages && (
                <Block align="center">
                    <Link
                        translation="sak.showAll"
                        onClick={toggleShowAllMessages}
                    />
                </Block>
            )}

            {showAllMessages && (
                <Block>
                    {sortedList.length > 1 &&
                        sortedList.map((element, index) =>
                            index > 0 ? (
                                <React.Fragment key={element.id}>
                                    <SaksmeldingComponent
                                        saksmelding={element}
                                    />
                                </React.Fragment>
                            ) : null,
                        )}

                    {sak.isArchived && (
                        <>
                            <Divider />
                            <Block
                                klpTypographyVariant="epsilon"
                                align="center"
                                marginTop="tiny"
                            >
                                {t('sak.sakLukketDato', {
                                    lukketDato: formatDateTime(sak.lukketDato),
                                })}
                            </Block>
                        </>
                    )}
                </Block>
            )}

            {showAllMessages && (
                <>
                    <Block marginTop="bulky" marginBottom="compact">
                        <Divider />
                    </Block>

                    <Block align="center">
                        <Link
                            translation="general.backToTop"
                            onClick={() => window.scrollTo(0, 0)}
                        />
                    </Block>
                </>
            )}
        </>
    );
};
